import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Container from '@mui/material/Container';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import LoginIcon from '@mui/icons-material/Login';
import { getEndpoint } from '../config';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(getEndpoint('LOGIN'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem('token', data.token);
        window.location.href = '/';
      } else {
        setError(data.error || 'خطا در ورود');
      }
    } catch (err) {
      setError('عدم دسترسی به سرور. لطفاً دوباره تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #F0F5F9 0%, #e5f3fb 50%, #F0F5F9 100%)',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23004d34" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        zIndex: 0,
      }
    }}>
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Card sx={{
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(0, 77, 52, 0.15)',
          border: '1px solid rgba(0, 77, 52, 0.1)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: 'linear-gradient(90deg, #004d34 0%, #30d3ae 50%, #004d34 100%)',
          }
        }}>
          <CardContent sx={{ p: 6 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #004d34 0%, #30d3ae 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                boxShadow: '0 8px 32px rgba(0, 77, 52, 0.3)',
              }}>
                <LoginIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 'bold', 
                color: '#004d34',
                fontFamily: 'Yekan, Vazir',
                mb: 1
              }}>
                ورود به پنل مدیریت
              </Typography>
              <Typography variant="body1" sx={{ 
                color: '#707a82',
                fontFamily: 'Yekan, Vazir',
                fontSize: '1rem'
              }}>
                لطفاً اطلاعات خود را وارد کنید
              </Typography>
            </Box>

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                label="نام کاربری"
                fullWidth
                margin="normal"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: '#004d34' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  fontFamily: 'Yekan, Vazir',
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                    fontSize: '1rem',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#004d34',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#004d34',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: 'Yekan, Vazir',
                    '&.Mui-focused': {
                      color: '#004d34',
                    },
                  },
                }}
                inputProps={{ 
                  style: { 
                    fontFamily: 'Yekan, Vazir',
                    padding: '16px 14px',
                  } 
                }}
              />
              
              <TextField
                label="رمز عبور"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                margin="normal"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: '#004d34' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                        sx={{ color: '#004d34' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  fontFamily: 'Yekan, Vazir',
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                    fontSize: '1rem',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#004d34',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#004d34',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: 'Yekan, Vazir',
                    '&.Mui-focused': {
                      color: '#004d34',
                    },
                  },
                }}
                inputProps={{ 
                  style: { 
                    fontFamily: 'Yekan, Vazir',
                    padding: '16px 14px',
                  } 
                }}
              />
              
              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3, 
                    fontFamily: 'Yekan, Vazir',
                    borderRadius: '12px',
                    backgroundColor: '#ffede9',
                    color: '#fb977d',
                    border: '1px solid rgba(251, 151, 125, 0.3)',
                    '& .MuiAlert-icon': {
                      color: '#fb977d',
                    }
                  }}
                >
                  {error}
                </Alert>
              )}
              
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{ 
                  fontFamily: 'Yekan, Vazir', 
                  fontWeight: 'bold', 
                  py: 2,
                  fontSize: '1.1rem',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #004d34 0%, #30d3ae 100%)',
                  boxShadow: '0 8px 32px rgba(0, 77, 52, 0.3)',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #003d2a 0%, #26b89a 100%)',
                    boxShadow: '0 12px 40px rgba(0, 77, 52, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    background: 'rgba(0, 77, 52, 0.3)',
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {loading ? 'در حال ورود...' : 'ورود به سیستم'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default Login; 